import { create } from 'zustand'
import { getEnv } from '@/lib/utils'
import { gatewayFetch } from '@/lib/gateway-auth'

export interface Agent {
  id: string
  name: string
  description?: string
  status: 'idle' | 'busy' | 'offline' | 'error'
  capabilities: string[]
  channels: string[]
  metadata: Record<string, unknown>
  registeredAt: number
  lastHeartbeatAt: number
}

export interface QueueStats {
  waiting: number
  active: number
  completed: number
  failed: number
  delayed: number
  paused: number
}

export interface RouterStats {
  messagesReceived: number
  messagesRouted: number
  messagesFailed: number
  messagesByType: Record<string, number>
  clients: number
  agents: number
  rules: number
  targets: number
  queues: number
}

interface AgentsState {
  agents: Agent[]
  queueStats: QueueStats | null
  routerStats: RouterStats | null
  isLoading: boolean
  error: string | null

  fetchAgents: () => Promise<void>
  registerAgent: (body: { name: string; description?: string; capabilities?: string[]; channels?: string[] }) => Promise<Agent | null>
  fetchQueueStats: () => Promise<void>
  fetchRouterStats: () => Promise<void>
}

function baseUrl(): string {
  const gw = getEnv('VITE_GATEWAY_URL').replace(/\/$/, '')
  return gw || '/api'
}

export const useAgentsStore = create<AgentsState>((set) => ({
  agents: [],
  queueStats: null,
  routerStats: null,
  isLoading: false,
  error: null,

  fetchAgents: async () => {
    set({ isLoading: true, error: null })
    try {
      const res = await gatewayFetch(`${baseUrl()}/agents`)
      if (!res.ok) throw new Error(`Failed to fetch agents: ${res.status}`)
      const data = await res.json()
      set({ agents: Array.isArray(data) ? data : data.agents || [], isLoading: false })
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false })
    }
  },

  registerAgent: async (body) => {
    try {
      const res = await gatewayFetch(`${baseUrl()}/agents/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error(`Failed to register agent: ${res.status}`)
      const agent = await res.json() as Agent
      set((state) => ({ agents: [...state.agents, agent] }))
      return agent
    } catch (e) {
      set({ error: (e as Error).message })
      return null
    }
  },

  fetchQueueStats: async () => {
    try {
      const res = await gatewayFetch(`${baseUrl()}/queues/stats`)
      if (!res.ok) return
      const data = await res.json()
      set({ queueStats: data })
    } catch {
      // non-critical
    }
  },

  fetchRouterStats: async () => {
    try {
      const res = await gatewayFetch(`${baseUrl()}/router/stats`)
      if (!res.ok) return
      const data = await res.json()
      set({ routerStats: data })
    } catch {
      // non-critical
    }
  },
}))
